import React from 'react';
import { FiAlertCircle, FiClock, FiCheckCircle, FiShield, FiZap, FiCalendar } from 'react-icons/fi';
import { clsx } from 'clsx';
import SafeIcon from '../common/SafeIcon';

const CongestionIntelligence = ({ stats, isRunning }) => {
  const { ping, jitter, dnsLatencies } = stats;

  // Logic for Current Congestion Level
  const getCongestionStatus = () => {
    if (ping > 100 || jitter > 20) return { label: 'Heavy Congestion', color: 'text-danger', bg: 'bg-danger/10', border: 'border-danger/20', icon: FiAlertCircle };
    if (ping > 50 || jitter > 10) return { label: 'Moderate Load', color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/20', icon: FiAlertCircle };
    return { label: 'Optimal Flow', color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20', icon: FiCheckCircle };
  };

  const status = getCongestionStatus();

  // Logic for Best DNS
  const getBestDNS = () => {
    const validDNS = Object.entries(dnsLatencies).filter(([_, v]) => v > 0);
    if (validDNS.length === 0) return { name: 'Detecting...', provider: '---' };
    
    const best = validDNS.reduce((prev, curr) => (prev[1] < curr[1] ? prev : curr));
    const names = { '1.1.1.1': 'Cloudflare', '8.8.8.8': 'Google', '9.9.9.9': 'Quad9' };
    return { name: names[best[0]], ip: best[0], latency: best[1] };
  };

  const bestDNS = getBestDNS();

  // Time-based recommendations (Simulated based on local hour)
  const currentHour = new Date().getHours();
  const timeInsights = [
    { range: '19:00 - 23:00', label: 'Peak Congestion', status: 'Avoid Large Syncs', isBad: currentHour >= 19 && currentHour <= 23 },
    { range: '02:00 - 06:00', label: 'Golden Window', status: 'Optimal for AI/SEO', isGood: currentHour >= 2 && currentHour <= 6 },
  ];

  return (
    <div className={clsx("space-y-6 mt-8", !isRunning && "opacity-50 grayscale pointer-events-none")}>
      <div className="flex items-center justify-between px-1">
        <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted flex items-center gap-2">
          <SafeIcon icon={FiAlertCircle} className="text-warning" /> Traffic & Congestion
        </h3>
        <span className="text-[9px] font-bold text-muted uppercase">Network Forecast</span>
      </div>

      <div className="glass-panel rounded-2xl p-4 border border-white/5 space-y-5">
        {/* Real-time Alert */}
        <div className={clsx("p-3 rounded-xl border flex items-center gap-3 transition-all", status.bg, status.border)}>
          <div className={clsx("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", status.bg, status.border)}>
            <SafeIcon icon={status.icon} className={status.color} size={16} />
          </div>
          <div>
            <div className={clsx("text-[10px] font-black uppercase tracking-widest", status.color)}>{status.label}</div>
            <p className="text-[11px] text-text/80 leading-tight">
              {ping < 40 ? "Edge nodes are clear. Low interference detected." : "Regional routing bottlenecks identified in local ISP nodes."}
            </p>
          </div>
        </div>

        {/* Time Windows */}
        <div className="space-y-3">
          <div className="text-[9px] font-black text-muted uppercase tracking-widest px-1">Usage Forecast</div>
          <div className="grid grid-cols-2 gap-3">
            {timeInsights.map((time, i) => (
              <div key={i} className={clsx("p-3 rounded-xl border bg-black/20", time.isBad ? "border-danger/30" : time.isGood ? "border-primary/30" : "border-white/5")}>
                <div className="flex items-center gap-1.5 mb-1 text-muted">
                  <SafeIcon icon={FiClock} size={10} />
                  <span className="text-[9px] font-bold uppercase">{time.range}</span>
                </div>
                <div className="text-[11px] font-black text-white leading-tight mb-0.5">{time.label}</div>
                <div className={clsx("text-[9px] font-bold uppercase", time.isBad ? "text-danger" : "text-primary/70")}>{time.status}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Best DNS Verdict */}
        <div className="pt-2">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 opacity-10 -mr-4 -mt-4">
              <SafeIcon icon={FiZap} size={80} className="text-primary" />
            </div>
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center border border-primary/30 shrink-0">
                <SafeIcon icon={FiShield} size={24} className="text-primary animate-pulse" />
              </div>
              <div>
                <div className="text-[10px] font-black text-primary uppercase tracking-widest mb-1">Recommended Private DNS</div>
                <h4 className="text-lg font-black text-white leading-none mb-1">{bestDNS.name}</h4>
                <p className="text-[11px] text-muted leading-relaxed max-w-[180px]">
                  Configuring <span className="text-primary font-mono">{bestDNS.ip}</span> will reduce your current resolution overhead by ~{Math.max(10, 100 - bestDNS.latency)}%.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CongestionIntelligence;